/*
Copyright 2021-2024 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type FC, type FormEvent, useCallback, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { Button } from "@vector-im/compound-web";

import Logo from "../icons/LogoLarge.svg?react";
import { useClient } from "../ClientContext";
import { FieldRow, InputField, ErrorMessage } from "../input/Input";
import styles from "./LoginPage.module.css";
import { useInteractiveLogin } from "./useInteractiveLogin";
import { usePageTitle } from "../usePageTitle";
import { PosthogAnalytics } from "../analytics/PosthogAnalytics";
import { Config } from "../config/Config";
import { Link } from "../button/Link";

export const LoginPage: FC = () => {
  const { t } = useTranslation();
  usePageTitle(t("login_title"));

  const { client, setClient } = useClient();
  const login = useInteractiveLogin(client);
  const homeserver = Config.defaultHomeserverUrl(); // TODO: Make this configurable
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();

  // TODO: Handle hitting login page with authenticated client

  const onSubmitLoginForm = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);

      if (!homeserver || !usernameRef.current || !passwordRef.current) {
        setError(Error("Login parameters are undefined"));
        setLoading(false);
        return;
      }

      login(homeserver, usernameRef.current.value, passwordRef.current.value)
        .then(async ([client, session]) => {
          if (!setClient) {
            return;
          }

          setClient(client, session);

          const locationState = location.state;
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          if (locationState && locationState.from) {
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            await navigate(locationState.from);
          } else {
            await navigate("/");
          }
          PosthogAnalytics.instance.eventLogin.track();
        })
        .catch((error) => {
          setError(error);
          setLoading(false);
        });
    },
    [login, location, navigate, homeserver, setClient],
  );
  // we need to limit the length of the homserver name to not cover the whole loginview input with the string.
  let shortendHomeserverName = Config.defaultServerName()?.slice(0, 25);
  shortendHomeserverName =
    shortendHomeserverName?.length !== Config.defaultServerName()?.length
      ? shortendHomeserverName + "..."
      : shortendHomeserverName;
  return (
    <>
      <div className={styles.container} style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        <div className={styles.content} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div style={{ position: 'absolute', top: '20px', left: '20px', zIndex: 1000, display: 'flex', alignItems: 'center', gap: '15px' }}>
            <img 
                src="/logo_mimosa_call.png" 
                alt="Mimosa Call" 
                style={{ 
                    height: "60px", 
                    width: "60px", 
                    borderRadius: "12px", 
                    boxShadow: "0 4px 8px rgba(0,0,0,0.2)"
                }} 
            />
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px' }}>
              <span style={{ fontSize: '45px', fontWeight: '700', color: '#ffffff', lineHeight: '60px' }}>Mimosa Call</span>
              <span style={{ fontSize: '18px', fontStyle: 'italic', color: '#00f3ff', fontWeight: '500', lineHeight: '60px' }}>Convergence Visioconférence</span>
            </div>
          </div>
          <div className={styles.formContainer}>
            <div 
                onClick={() => {
                window.location.href = `https://ma3x.ztn0.net/_matrix/client/v3/login/sso/redirect?redirectUrl=${encodeURIComponent(window.location.origin)}`;
                }}
                data-testid="login_sso"
                style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                cursor: 'pointer'
                }}
            >
                <img src="/fongola_icon.png" alt="Fongola SSO" style={{ width: '120px', height: '120px', marginBottom: '16px', transition: 'transform 0.2s', borderRadius: '30px', boxShadow: '0 4px 12px rgba(0,0,0,0.3)' }} onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'} onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'} />
                <span style={{ fontSize: '18px', fontWeight: '500', color: '#9ca3af' }}>Se connecter avec Convergence</span>
            </div>
          </div>
        </div>
        <footer style={{
          padding: '20px',
          textAlign: 'center',
          fontSize: '13px',
          color: 'rgba(255, 255, 255, 0.45)',
          lineHeight: '1.6',
          width: '100%',
          marginTop: 'auto',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
          background: 'transparent'
        }}>
          <div>Propulsé par D3X Integral Portal</div>
          <div style={{ fontWeight: '500', color: 'rgba(255, 255, 255, 0.35)' }}>L'accès à ce système est restreint.</div>
          <div style={{ marginTop: '8px', color: 'rgba(255, 255, 255, 0.25)' }}>© 2026 Convergence Cloud Privé</div>
        </footer>
      </div>
    </>
  );
};
